// Constants
export const DEFAULT_HLD_DISTANCE_METRES = 7000;

export enum CPTSlab {
  HLD = 1,
  LOCAL = 2,
  OUTSIDE = 3,
}

export enum CPTLabel {
  HLD = "HLD",
  Local = "Local",
  Outside = "Outside",
}

export const CPTSlabToLabel: Record<CPTSlab, CPTLabel> = {
  [CPTSlab.HLD]: CPTLabel.HLD,
  [CPTSlab.LOCAL]: CPTLabel.Local,
  [CPTSlab.OUTSIDE]: CPTLabel.Outside,
};

export const LabelToCPTSlab: Record<CPTLabel, CPTSlab> = {
  [CPTLabel.HLD]: CPTSlab.HLD,
  [CPTLabel.Local]: CPTSlab.LOCAL,
  [CPTLabel.Outside]: CPTSlab.OUTSIDE,
};

export interface CPTSlabOption {
  value: CPTSlab;
  label: CPTLabel;
  slabLevel: number;
}

export interface OrderCptSlabDto {
  slabLevel: number;
  label: CPTLabel;
}

const normalizeCityName = (city: string): string => {
  return city.toLowerCase().trim();
};

const shouldApplyLocalSlab = (
  orderShipmentCity?: string,
  supplierCity?: string
): boolean => {
  const hasorderShipmentCity = !!orderShipmentCity?.trim();
  const hasSupplierCity = !!supplierCity?.trim();

  if (!hasorderShipmentCity && !hasSupplierCity) {
    return true;
  }

  if (hasorderShipmentCity && !hasSupplierCity) {
    return true;
  }
  if (!hasorderShipmentCity && hasSupplierCity) {
    return true;
  }

  if (
    hasorderShipmentCity &&
    hasSupplierCity &&
    orderShipmentCity &&
    supplierCity
  ) {
    return (
      normalizeCityName(orderShipmentCity) === normalizeCityName(supplierCity)
    );
  }

  return false;
};

const metresToKilometres = (metres: number): number => {
  return metres / 1000;
};

const isWithinHLDDistance = (
  distanceKm?: number,
  hldDistanceMetres: number = DEFAULT_HLD_DISTANCE_METRES
): boolean => {
  if (distanceKm === undefined || distanceKm === Infinity) {
    return false;
  }
  const hldDistanceKm = metresToKilometres(hldDistanceMetres);
  return distanceKm <= hldDistanceKm;
};

export const getCPTSlabLabel = (slab: CPTSlab): CPTLabel => {
  return CPTSlabToLabel[slab] || CPTLabel.Outside;
};

export const getCPTSlabOptions = (): CPTSlabOption[] => {
  return [CPTSlab.HLD, CPTSlab.LOCAL, CPTSlab.OUTSIDE].map((slab) => ({
    value: slab,
    label: getCPTSlabLabel(slab),
    slabLevel: slab,
  }));
};

export const cptSlabToDto = (slab: CPTSlab): OrderCptSlabDto => {
  return {
    slabLevel: slab,
    label: getCPTSlabLabel(slab),
  };
};

export const calculateSupplierCPTSlab = (
  orderData: any,
  distanceKm?: number,
  supplierCity?: string,
  hldDistanceMetres: number = DEFAULT_HLD_DISTANCE_METRES
): CPTSlab => {
  const orderShipmentCity = orderData?.shipmentAddress?.city;

  if (isWithinHLDDistance(distanceKm, hldDistanceMetres)) {
    return CPTSlab.HLD;
  }

  if (shouldApplyLocalSlab(orderShipmentCity, supplierCity)) {
    return CPTSlab.LOCAL;
  }

  return CPTSlab.OUTSIDE;
};

export const calculateCPTSlabsForSuppliers = <
  T extends { distanceKm?: number; supplierCity?: string }
>(
  orderData: any,
  suppliers: T[],
  hldDistanceMetres: number = DEFAULT_HLD_DISTANCE_METRES
): Array<T & { cptSlab: CPTSlab }> => {
  return suppliers.map((supplier) => ({
    ...supplier,
    cptSlab: calculateSupplierCPTSlab(
      orderData,
      supplier.distanceKm,
      supplier.supplierCity,
      hldDistanceMetres
    ),
  }));
};
